import React from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import {CollapsiblePanel} from 'entities/tariff';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import HintQuestion from 'shared/ui/Hint/HintQuestion';
import RadioGroup from 'shared/ui/RadioGroup/RadioGroup';
import {TagsChipField, BatchResult, CheckInterval, GeoCheck} from 'entities/monitor';
import {NotifyChannels} from 'entities/notification';
import TextArea from 'shared/ui/TextArea/TextArea';
import TimeoutSlider from 'shared/ui/Slider/TimeoutSlider';
import {parsePortTargets} from 'features/port/lib/parsePortTargets';
import {PORT_DEFAULT_TIMEOUT} from 'features/port/lib/portMonitorFormHelper';
import {usePortMonitorBatchCreateForm} from 'features/port/model/usePortMonitorBatchCreateForm';

import styles from './PortMonitorForm.module.scss';

interface IPortMonitorBatchCreateFormProps {
    handleClose: () => void,
    // eslint-disable-next-line no-unused-vars
    onShowResultChange?: (isShowing: boolean) => void,
}

const PortMonitorBatchCreateForm: React.FC<IPortMonitorBatchCreateFormProps> = ({
    handleClose,
    onShowResultChange,
}) => {
    const {form, result, isLoading, progress, submit, handleCloseResult} = usePortMonitorBatchCreateForm(
        handleClose,
        onShowResultChange,
    );
    const {register, formState, control, setValue, watch} = form;
    const {errors, isValid} = formState;

    if (result) {
        return (
            <BatchResult
                result={result}
                handleClose={handleCloseResult}
                title={t`Мониторы портов добавлены`}
            />
        );
    }

    return (
        <form onSubmit={submit} className={styles.form}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    error={errors?.targets?.message}
                    className={cn(styles.formGroup, styles.firstFormGroup)}
                >
                    <TextArea
                        rows={5}
                        register={register}
                        placeholder={t`Вводите каждый адрес с новой строки в формате ip:port`}
                        name="targets"
                        constraints={{
                            validate: (value: string) =>
                                parsePortTargets(value).length > 0 || t`Укажите хотя бы один адрес`,
                        }}
                    />
                </FormGroupWithError>

                <TagsChipField
                    control={control}
                    label={t`Теги`}
                    error={errors?.tags?.message}
                    className={styles.formGroup}
                />

                <div className={styles.block}>
                    <div className={styles.label}>
                        {t`Желаемое состояние`}
                        <HintQuestion placement="top-start" className={styles.icon}>
                            {t`Инцидент откроется, когда порт окажется не в этом состоянии.`}
                        </HintQuestion>
                    </div>
                    <RadioGroup
                        name="desired_state"
                        control={control}
                        options={[
                            {value: 'open', label: t`Открыт`},
                            {value: 'closed', label: t`Закрыт`},
                        ]}
                    />
                </div>

                <CheckInterval item={undefined} setValue={setValue} watch={watch} errors={errors}/>

                <div className={styles.block}>
                    <div className={styles.label}>
                        {t`Таймаут проверки`}
                    </div>
                    <TimeoutSlider
                        name="timeout"
                        setValue={setValue}
                        defaultValue={PORT_DEFAULT_TIMEOUT}
                    />
                </div>

                <GeoCheck control={control} setValue={setValue}/>
            </ModalBodyWrapper>

            <ModalBodyWrapper>
                <CollapsiblePanel
                    title={t`Уведомления`}
                    defaultExpanded={false}
                    className={styles.panel}
                >
                    <NotifyChannels
                        control={control}
                        register={register}
                        emailFieldName="notify_by_email"
                        additionalEmailsFieldName="additional_emails"
                        telegramFieldName="telegram_chats"
                        maxFieldName="max_chats"
                    />
                </CollapsiblePanel>
            </ModalBodyWrapper>

            <ModalBlockButton
                primaryButtonText={t`Добавить`}
                primaryButtonType="submit"
                primaryButtonLoading={isLoading}
                primaryButtonDisabled={!isValid}
                secondaryButtonOnClick={handleClose}
                progress={progress}
            />
        </form>
    );
};

export default PortMonitorBatchCreateForm;
